import { BlockedRecordBlockedScope } from '@exam-center/graphql';
import {
  Box,
  Flex,
  FormControl,
  FormDateInput,
  FormHelperText,
  FormRadio,
  FormRadioSelection,
  FormSearchSelection,
  Grid,
  type OptionSelectionProps,
  RadioGroup,
  Text,
} from '@shared/components';

import {
  BlockedCoursesSelectionField,
  type BlockedCoursesSelectionFieldProps,
} from '../blocked-courses-selection-field';
import {
  BlockedEndDateSettingSection,
  type BlockedEndDateSettingSectionProps,
} from '../blocked-end-date-setting-section';
import { BlockedReasonField } from '../blocked-reason-field';

const BLOCKED_SCOPE_OPTIONS: OptionSelectionProps[] = [
  {
    label: 'ระงับสิทธิ์ทุกหลักสูตร',
    value: BlockedRecordBlockedScope.AllScope,
  },
  { label: 'ระงับสิทธิ์เฉพาะหลักสูตรที่เลือก', value: BlockedRecordBlockedScope.PartialScope },
];

interface BasicFields {
  blockedCourse: {
    type: {
      name: string;
    };
    courses: BlockedCoursesSelectionFieldProps;
  };
  reason: {
    name: string;
  };
}

export interface BlockedInformationCreationFieldsProps {
  fields: BasicFields & {
    blockedAt: {
      name: string;
      required?: boolean;
      blockedDuration?: string;
    };
    enforcementEnd: BlockedEndDateSettingSectionProps & {
      assign: {
        name: string;
        error?: string;
      };
    };
    examCenter?: {
      name: string;
      options: Array<{ label: string; value: string }>;
      loading: boolean;
      required?: boolean;
    };
  };
  slots?: {
    blockedAtLabel?: string;
  };
  visibleFields?: {
    examCenter?: boolean;
  };
  options?: {
    blockedUntilMode: 'duration' | 'date';
  };
}

export default function BlockedInformationCreationFields(
  props: Readonly<BlockedInformationCreationFieldsProps>,
) {
  const {
    fields,
    slots,
    visibleFields,
    options = {
      blockedUntilMode: 'duration',
    },
  } = props;

  const isBlockedAtDurationMode = options?.blockedUntilMode === 'duration';

  const blockedAtLabel = slots?.blockedAtLabel ?? 'วันที่เริ่มปฏิบัติหน้าที่';

  return (
    <Grid
      sx={{
        gridTemplateColumns: '240px 1fr',
        gap: (theme) => theme.spacing(6),
      }}
    >
      <Text variant='body1Semibold'>{blockedAtLabel}</Text>
      <Box sx={{ maxWidth: '212px' }}>
        <FormDateInput
          {...fields.blockedAt}
          label={blockedAtLabel}
          placeholder='DD-MM-YYYY'
          inputFormat='DD-MM-YYYY'
        />
      </Box>
      {isBlockedAtDurationMode ? (
        <>
          <Text variant='body1Semibold'>วันที่สิ้นสุดปฏิบัติหน้าที่</Text>
          <FormControl error={!!fields.enforcementEnd.assign.error}>
            <RadioGroup
              name={fields.enforcementEnd.assign.name}
              sx={(theme) => ({
                gap: theme.spacing(4),
              })}
            >
              <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(2) })}>
                <FormRadio
                  {...fields.enforcementEnd.assign}
                  label='ไม่มีกำหนด'
                  value='false'
                  radioProps={{
                    sx: (theme) => ({
                      py: theme.spacing(0),
                    }),
                  }}
                />
                <Text
                  variant='caption'
                  sx={(theme) => ({ ml: theme.spacing(8), color: theme.palette.text.secondary })}
                >
                  ยังไม่มีกำหนดวันที่สิ้นสุดปฏิบัติหน้าที่
                </Text>
              </Flex>
              <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(2) })}>
                <FormRadio
                  {...fields.enforcementEnd.assign}
                  label='กำหนดวันที่สิ้นสุดปฏิบัติหน้าที่'
                  value='true'
                  radioProps={{
                    sx: (theme) => ({
                      py: theme.spacing(0),
                    }),
                  }}
                />
                <Flex
                  sx={(theme) => ({
                    flexDirection: 'column',
                    ml: theme.spacing(8),
                    gap: theme.spacing(2.5),
                  })}
                >
                  <Text variant='caption' sx={(theme) => ({ color: theme.palette.text.secondary })}>
                    คำนวณจากระยะเวลาปฏิบัติหน้าที่หรือระบุวันที่สิ้นสุดปฏิบัติหน้าที่ด้วยตนเอง
                  </Text>
                  <BlockedEndDateSettingSection
                    duration={fields.enforcementEnd.duration}
                    date={fields.enforcementEnd.date}
                    blockedUntil={fields.enforcementEnd.blockedUntil}
                  />
                </Flex>
              </Flex>
              {fields.enforcementEnd.assign.error ? (
                <FormHelperText>{fields.enforcementEnd.assign.error}</FormHelperText>
              ) : null}
            </RadioGroup>
          </FormControl>
        </>
      ) : (
        <>
          <Text variant='body1Semibold'>วันที่สิ้นสุดระงับสิทธิ์</Text>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Box sx={{ flexBasis: '212px' }}>
              <FormDateInput
                name={fields.enforcementEnd.date.name}
                required={fields.enforcementEnd.date.required}
                label='วันที่สิ้นสุดระงับสิทธิ์'
                placeholder='DD-MM-YYYY'
                inputFormat='DD-MM-YYYY'
                minDate={new Date()}
              />
            </Box>
            {fields.blockedAt.blockedDuration ? (
              <Text variant='body2' sx={(theme) => ({ alignSelf: 'end', pb: theme.spacing(4) })}>
                (รวมระยะเวลาระงับสิทธิ์ทั้งหมด: {fields.blockedAt.blockedDuration} )
              </Text>
            ) : null}
          </Flex>
        </>
      )}

      <Text variant='body1Semibold'>หลักสูตรที่ระงับสิทธิ์สอบ</Text>
      <Flex sx={{ flexDirection: 'column' }}>
        <FormRadioSelection
          {...fields.blockedCourse.type}
          options={BLOCKED_SCOPE_OPTIONS}
          radioProps={{
            sx: (theme) => ({
              py: theme.spacing(0),
            }),
          }}
        />
        <BlockedCoursesSelectionField {...fields.blockedCourse.courses} />
      </Flex>

      {visibleFields?.examCenter && fields.examCenter ? (
        <>
          <Text variant='body1Semibold'>ศูนย์ทดสอบ</Text>
          <FormSearchSelection
            {...fields.examCenter}
            label='ศูนย์ทดสอบ'
            placeholder='เลือกศูนย์ทดสอบ'
            sx={{ maxWidth: '600px' }}
          />
        </>
      ) : null}
      <Text variant='body1Semibold'>หมายเหตุ</Text>
      <BlockedReasonField {...fields.reason} />
    </Grid>
  );
}
